/**
 * Why a card was declined: one plain line per reason (DNKADM-0466, contract DNKAPI-0676).
 *
 * dink-api maps the payment provider's decline code to a fixed `declineReason` and returns
 * it on:
 *  - the staff `402 PAYMENT_DECLINED` envelope (`declineReason`, present only when known;
 *    kept on `ApiError.details.declineReason` by `apiErrorFromBody`);
 *  - payment ledger rows (`GET /v2/admin/payments/transactions`);
 *  - member timeline payment items (`details.declineReason`);
 *  - admin pending charges and the reservation's inline pending-charge summary.
 *
 * It may be absent or null (dink does not know why), and `OTHER` means the provider gave a
 * code that has no line here. Both read as "no line": each surface keeps the copy it showed
 * before this change. An unknown future value is treated the same way. The provider's own
 * message never reaches the admin (the API never sends it), so nothing here renders one.
 */
import { ApiError } from '@/api/errors';

export const DECLINE_REASONS = [
  'ZIP_MISMATCH',
  'CVV_MISMATCH',
  'EXPIRED',
  'INSUFFICIENT_FUNDS',
  'CALL_BANK',
  'CARD_NOT_SUPPORTED',
  'LIMIT',
  'OTHER',
] as const;

export type DeclineReason = (typeof DECLINE_REASONS)[number];

/** The staff line per reason. `OTHER` has none on purpose: it never claims a specific cause. */
const DECLINE_REASON_LINES: Readonly<Record<Exclude<DeclineReason, 'OTHER'>, string>> = {
  ZIP_MISMATCH: "The billing zip code didn't match the card. Check the zip and try again.",
  CVV_MISMATCH: "The security code didn't match the card. Check the code and try again.",
  EXPIRED: 'The card is expired or the expiration date is wrong. Check the date or use another card.',
  INSUFFICIENT_FUNDS:
    "The card doesn't have enough available funds or credit for this amount. Try a smaller amount or another card.",
  CALL_BANK:
    'The bank declined this card. Ask the customer to call their bank or use another card.',
  CARD_NOT_SUPPORTED: "This type of card can't be used here. Ask the customer for another card.",
  // Covers both the seller's processing limit and the card issuer's, so it names neither.
  LIMIT: 'This amount is over a payment limit. Try a smaller amount or another card.',
};

/** A known `DeclineReason`, or null for a missing, null, blank or unknown value. */
export function parseDeclineReason(value: unknown): DeclineReason | null {
  if (typeof value !== 'string') return null;
  const text = value.trim();
  return (DECLINE_REASONS as readonly string[]).includes(text) ? (text as DeclineReason) : null;
}

/**
 * The staff line for a reason, or null when there is none (`OTHER`, null, unknown). The
 * caller shows its own existing decline copy on null.
 */
export function declineReasonLine(reason: DeclineReason | null | undefined): string | null {
  if (reason === null || reason === undefined || reason === 'OTHER') return null;
  return DECLINE_REASON_LINES[reason];
}

/** The reason line carried on a staff `402 PAYMENT_DECLINED` error, or null. */
export function declineReasonLineFromError(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  return declineReasonLine(parseDeclineReason(error.details?.['declineReason']));
}
